import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ContentHeader from '../components/ContentHeader';
import { staffAPI, workTasksAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import './NotifyStaff.css';

const MAX_MESSAGE = 8000;

function staffDisplayName(s) {
  const n = [s.last_name, s.first_name].filter(Boolean).join(', ');
  return n || `Staff #${s.id}`;
}

function NotifyStaff() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const canSend = hasPermission('notify_staff.send');

  useEffect(() => {
    if (!canSend) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadError(null);
        const res = await staffAPI.getAll({ active: 'true' });
        if (cancelled) return;
        const rows = res.data?.data || [];
        setStaff(rows);
      } catch (e) {
        if (!cancelled) setLoadError(e.response?.data?.error || 'Failed to load staff');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canSend]);

  const sortedStaff = useMemo(
    () =>
      [...staff].sort((a, b) =>
        staffDisplayName(a).toLowerCase().localeCompare(staffDisplayName(b).toLowerCase())
      ),
    [staff]
  );

  const allIds = useMemo(() => sortedStaff.map((s) => s.id), [sortedStaff]);

  const toggleOne = useCallback((id) => {
    setSelectAllMode(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const onToggleSelectAll = useCallback(
    (checked) => {
      if (checked) {
        setSelectAllMode(true);
        setSelected(new Set(allIds));
      } else {
        setSelectAllMode(false);
        setSelected(new Set());
      }
    },
    [allIds]
  );

  const allChecked = selectAllMode && allIds.length > 0 && selected.size === allIds.length;
  const someChecked = selected.size > 0 && !allChecked;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSendResult(null);
    const trimmed = message.trim();
    if (!trimmed) {
      setSendResult({ ok: false, text: 'Enter a message to send.' });
      return;
    }
    if (trimmed.length > MAX_MESSAGE) {
      setSendResult({ ok: false, text: `Message must be at most ${MAX_MESSAGE} characters.` });
      return;
    }
    if (!selectAllMode && selected.size === 0) {
      setSendResult({ ok: false, text: 'Select all staff, or choose one or more recipients.' });
      return;
    }

    try {
      setSending(true);
      const body = selectAllMode
        ? { all: true, message: trimmed }
        : { staff_ids: Array.from(selected), message: trimmed };
      const res = await workTasksAPI.broadcastStaffNotification(body);
      const d = res.data?.data || {};
      const created = d.created ?? 0;
      const failed = d.failed ?? 0;
      if (failed > 0) {
        setSendResult({
          ok: true,
          text: `Sent ${created} notification(s). ${failed} failed (see server logs for details).`,
        });
      } else {
        setSendResult({ ok: true, text: `Sent ${created} notification(s). Recipients will see them on the dashboard under Notifications.` });
      }
      setMessage('');
      setSelectAllMode(false);
      setSelected(new Set());
    } catch (err) {
      setSendResult({
        ok: false,
        text: err.response?.data?.error || err.message || 'Send failed',
      });
    } finally {
      setSending(false);
    }
  };

  if (!canSend) {
    return (
      <div className="notify-staff-page">
        <ContentHeader title="Notify staff" />
        <p className="notify-staff-denied">
          You don&apos;t have permission to notify staff. This action is limited to roles that have been granted{' '}
          <strong>Notify Staff</strong> in Settings → Roles &amp; Permissions (typically administrators and managers).
        </p>
      </div>
    );
  }

  return (
    <div className="notify-staff-page">
      <ContentHeader title="Notify staff" />
      <p className="notify-staff-intro">
        Send a message to selected team members. Each person gets a notification on their <strong>Dashboard</strong>{' '}
        in the <strong>Notifications</strong> panel (same place as assignments and other alerts).
      </p>

      {loading && <p>Loading staff…</p>}
      {loadError && <p className="notify-staff-result err">{loadError}</p>}

      {!loading && !loadError && (
        <form onSubmit={handleSubmit}>
          <div className="notify-staff-field">
            <span id="notify-recipients-label">Recipients</span>
            <div className="notify-staff-all-row">
              <input
                type="checkbox"
                id="notify-staff-all"
                checked={allChecked}
                ref={(el) => {
                  if (el) el.indeterminate = someChecked;
                }}
                onChange={(e) => onToggleSelectAll(e.target.checked)}
                aria-labelledby="notify-recipients-label notify-staff-all-label"
              />
              <label id="notify-staff-all-label" htmlFor="notify-staff-all">
                <strong>Select all</strong> ({sortedStaff.length} active staff)
              </label>
            </div>
            <div className="notify-staff-list" role="group" aria-labelledby="notify-recipients-label">
              {sortedStaff.length === 0 ? (
                <p className="notify-staff-intro">No active staff found for this tenant.</p>
              ) : (
                sortedStaff.map((s) => (
                  <div key={s.id} className="notify-staff-row">
                    <input
                      type="checkbox"
                      id={`notify-staff-${s.id}`}
                      checked={selected.has(s.id)}
                      onChange={() => toggleOne(s.id)}
                    />
                    <label htmlFor={`notify-staff-${s.id}`}>{staffDisplayName(s)}</label>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="notify-staff-field">
            <label htmlFor="notify-staff-message">Message</label>
            <textarea
              id="notify-staff-message"
              className="notify-staff-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={MAX_MESSAGE}
              placeholder="Type the message you want recipients to read…"
              required
            />
            <div className="notify-staff-intro" style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
              {message.length} / {MAX_MESSAGE} characters
            </div>
          </div>

          <div className="notify-staff-actions">
            <button type="submit" className="btn-primary" disabled={sending || sortedStaff.length === 0}>
              {sending ? 'Sending…' : 'Send notifications'}
            </button>
            {sendResult && (
              <p className={`notify-staff-result ${sendResult.ok ? 'ok' : 'err'}`}>{sendResult.text}</p>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

export default NotifyStaff;
